/*
 * <product-list-scroller>
 * Horizontal circle list with prev/next arrows.
 * - Centered when the items fit; when they overflow the 1440px inner width,
 *   arrows appear and scroll the track (no loop — stops at both ends).
 * - Arrows enable/disable at the start/end of the scroll range.
 * - Desktop only (the element is hidden on mobile via CSS).
 * - connected/disconnected callbacks keep it Shopify theme-editor safe and
 *   independent across multiple instances; no globals.
 */
class ProductListScroller extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.product-list__track');
    this.prevButton = this.querySelector('.product-list__arrow--prev');
    this.nextButton = this.querySelector('.product-list__arrow--next');

    if (!this.track) return;

    this.onPrev = () => this.scrollByStep(-1);
    this.onNext = () => this.scrollByStep(1);
    this.onScroll = () => this.updateArrows();
    this.refresh = () => this.checkOverflow();

    this.prevButton?.addEventListener('click', this.onPrev);
    this.nextButton?.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.refresh);
      this.resizeObserver.observe(this.track);
    } else {
      window.addEventListener('resize', this.refresh);
    }

    this.checkOverflow();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this.onPrev);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.refresh);
  }

  checkOverflow() {
    if (!this.track) return;
    const overflowing = this.track.scrollWidth > this.track.clientWidth + 1;
    this.classList.toggle('is-overflowing', overflowing);
    this.updateArrows();
  }

  updateArrows() {
    if (!this.prevButton || !this.nextButton) return;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const current = this.track.scrollLeft;
    this.prevButton.disabled = current <= 1;
    this.nextButton.disabled = current >= maxScroll - 1;
  }

  scrollByStep(direction) {
    const step = Math.max(160, this.track.clientWidth * 0.8);
    this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }
}

if (!customElements.get('product-list-scroller')) {
  customElements.define('product-list-scroller', ProductListScroller);
}
